import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const PricingConfigurationTab = () => {
  const [editingCell, setEditingCell] = useState(null);
  const [pricingData, setPricingData] = useState({
    clientCategories: [
      {
        id: 1,
        category: 'individual',
        label: 'Particulier',
        simple: 45.50,
        handleless: 52.75
      },
      {
        id: 2,
        category: 'professional',
        label: 'Professionnel',
        simple: 38.25,
        handleless: 44.80
      },
      {
        id: 3,
        category: 'subcontractor',
        label: 'Sous-traitant',
        simple: 32.90,
        handleless: 38.15
      }
    ],
    worktopTypes: [
      {
        id: 1,
        type: 'macif',
        label: 'Macif',
        simple: 125.00,
        lShape: 185.50,
        uShape: 245.75
      },
      {
        id: 2,
        type: 'compact',
        label: 'Compact',
        simple: 98.25,
        lShape: 148.90,
        uShape: 198.50
      },
      {
        id: 3,
        type: 'laminate',
        label: 'Stratifié',
        simple: 65.80,
        lShape: 95.25,
        uShape: 125.60
      }
    ],
    serviceRates: [
      {
        id: 1,
        service: 'plumbing',
        label: 'Modifications plomberie',
        rate: 150.00
      },
      {
        id: 2,
        service: 'electrical',
        label: 'Modifications électriques',
        rate: 120.00
      },
      {
        id: 3,
        service: 'lighting',
        label: 'Installation éclairage',
        rate: 85.00
      }
    ]
  });

  const [tempValue, setTempValue] = useState('');

  const handleCellEdit = (section, id, field, currentValue) => {
    setEditingCell({ section, id, field });
    setTempValue(currentValue?.toString());
  };

  const handleSaveEdit = () => {
    if (!editingCell) return;

    const { section, id, field } = editingCell;
    const numericValue = parseFloat(tempValue?.replace(',', '.'));

    if (isNaN(numericValue) || numericValue < 0) {
      alert('Veuillez saisir une valeur numérique valide');
      return;
    }

    setPricingData(prev => ({
      ...prev,
      [section]: prev?.[section]?.map(item =>
        item?.id === id ? { ...item, [field]: numericValue } : item
      )
    }));

    setEditingCell(null);
    setTempValue('');
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setTempValue('');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })?.format(price);
  };

  const renderEditableCell = (section, id, field, value) => {
    const isEditing = editingCell?.section === section && 
                     editingCell?.id === id && 
                     editingCell?.field === field;

    if (isEditing) {
      return (
        <div className="flex items-center space-x-2">
          <Input
            type="text"
            value={tempValue}
            onChange={(e) => setTempValue(e?.target?.value)}
            className="w-24 h-8 text-sm"
            placeholder="0,00"
          />
          <Button
            variant="ghost"
            size="xs"
            iconName="Check"
            iconSize={14}
            onClick={handleSaveEdit}
            className="text-success hover:text-success"
          />
          <Button
            variant="ghost"
            size="xs"
            iconName="X"
            iconSize={14}
            onClick={handleCancelEdit}
            className="text-error hover:text-error"
          />
        </div>
      );
    }

    return (
      <button
        onClick={() => handleCellEdit(section, id, field, value)}
        className="text-left hover:bg-muted px-2 py-1 rounded transition-smooth group"
      >
        <span className="font-medium">{formatPrice(value)}</span>
        <Icon 
          name="Edit2" 
          size={12} 
          className="ml-2 opacity-0 group-hover:opacity-100 transition-smooth inline" 
        />
      </button>
    );
  };

  return (
    <div className="space-y-8">
      {/* Client Category Pricing */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Tarification par catégorie client</h3>
            <p className="text-sm text-text-secondary mt-1">
              Prix par élément selon le type de client et style de cuisine
            </p>
          </div>
          <Button variant="outline" size="sm" iconName="Download">
            Exporter
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-text-primary">Catégorie client</th>
                <th className="text-center py-3 px-4 font-medium text-text-primary">Style simple</th>
                <th className="text-center py-3 px-4 font-medium text-text-primary">Style sans poignées</th>
              </tr>
            </thead>
            <tbody>
              {pricingData?.clientCategories?.map((category) => (
                <tr key={category?.id} className="border-b border-border hover:bg-muted/50">
                  <td className="py-4 px-4">
                    <span className="font-medium text-text-primary">{category?.label}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    {renderEditableCell('clientCategories', category?.id, 'simple', category?.simple)}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {renderEditableCell('clientCategories', category?.id, 'handleless', category?.handleless)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Worktop Pricing */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Tarification plans de travail</h3>
            <p className="text-sm text-text-secondary mt-1">
              Prix selon le type de plan de travail et configuration d'installation
            </p>
          </div>
          <Button variant="outline" size="sm" iconName="Download">
            Exporter
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-text-primary">Type de plan</th>
                <th className="text-center py-3 px-4 font-medium text-text-primary">Installation simple</th>
                <th className="text-center py-3 px-4 font-medium text-text-primary">Installation en L</th>
                <th className="text-center py-3 px-4 font-medium text-text-primary">Installation en U</th>
              </tr>
            </thead>
            <tbody>
              {pricingData?.worktopTypes?.map((worktop) => (
                <tr key={worktop?.id} className="border-b border-border hover:bg-muted/50">
                  <td className="py-4 px-4">
                    <span className="font-medium text-text-primary">{worktop?.label}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    {renderEditableCell('worktopTypes', worktop?.id, 'simple', worktop?.simple)}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {renderEditableCell('worktopTypes', worktop?.id, 'lShape', worktop?.lShape)}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {renderEditableCell('worktopTypes', worktop?.id, 'uShape', worktop?.uShape)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Service Rates */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Tarifs services additionnels</h3>
            <p className="text-sm text-text-secondary mt-1">
              Prix fixes pour les services d'installation et modifications
            </p>
          </div>
          <Button variant="outline" size="sm" iconName="Plus">
            Ajouter service
          </Button>
        </div>

        <div className="space-y-4">
          {pricingData?.serviceRates?.map((service) => (
            <div key={service?.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50">
              <div>
                <span className="font-medium text-text-primary">{service?.label}</span>
              </div>
              <div className="flex items-center space-x-4">
                {renderEditableCell('serviceRates', service?.id, 'rate', service?.rate)}
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  iconSize={16}
                  className="text-error hover:text-error"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingConfigurationTab;